import React, { FunctionComponent, useMemo } from 'react';
import { Chip, SxProps, Theme } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { AStatusActionCard } from '@atiautomacao/ati-ui-library';
import { MenuItemStatus } from '../../../../../../Shared/Components/Layout/menu/types/MenuItemStatus';
import { ALARM_SEVERITY } from '../../../../../../Utils/AlarmsService';
import { FindActiveTeleObjectAlarmsByEquipmentId, NON_APPLICABLE_SEVERITY_ID } from '../../InverterReactQuery';
import { buildColorTintSx } from '../cardColorTint';
import { resolveAlarmStatusCard, SeverityInfo } from './alarmStatusCardState';

export interface InverterAlarmStatusCardProps {
    equipmentId: number;
    status?: MenuItemStatus;
}

const SeverityChip: FunctionComponent<{ severity: SeverityInfo }> = ({ severity }) => (
    <Chip
        label={severity.name.toUpperCase()}
        size="small"
        sx={{
            backgroundColor: `#${severity.color}`,
            // Texto branco nos fundos escuros (Crítica, Urgente); preto nos claros (Alta/Média/Baixa) — como no Figma.
            color:
                severity.id === ALARM_SEVERITY.CRITICAL || severity.id === ALARM_SEVERITY.URGENT
                    ? '#FFFFFF'
                    : '#000000',
            fontWeight: 600,
        }}
    />
);

/**
 * Card de estado de alarmes da tela simplificada de inversor.
 *
 * Container do `AStatusActionCard`: resolve o estado a partir do `etsStatus` (desabilitado em Sem
 * comunicação/Sincronizando/Inibido) e, quando comunicando, da maior severidade ativa (via `level`).
 * Os alarmes ativos vêm do mesmo hook já usado nesta tela.
 */
const InverterAlarmStatusCard: FunctionComponent<InverterAlarmStatusCardProps> = ({ equipmentId, status }) => {
    const { data } = FindActiveTeleObjectAlarmsByEquipmentId(equipmentId);

    const activeSeverityIds = useMemo(
        () =>
            (data?.data ?? [])
                .map((alarm) => Number(alarm.severityId))
                .filter((severityId) => severityId !== NON_APPLICABLE_SEVERITY_ID),
        [data]
    );

    const resolution = useMemo(
        () => resolveAlarmStatusCard(status, activeSeverityIds),
        [status, activeSeverityIds]
    );

    const isInhibited = resolution.state === 'INHIBITED';
    const isAlarmed = resolution.state === 'ALARMED';
    const isNormalized = resolution.state === 'NORMALIZED';

    let icon = <ErrorOutlineIcon />;
    if (isAlarmed) {
        icon = <WarningAmberOutlinedIcon />;
    } else if (isNormalized) {
        icon = <CheckCircleOutlineIcon />;
    }

    let tone: 'error' | 'success' | 'muted' = 'muted';
    if (isAlarmed) {
        tone = 'error';
    } else if (isNormalized) {
        tone = 'success';
    }

    let action: React.ReactNode;
    if (isInhibited) {
        action = <Chip label="INIBIDO" size="small" />;
    } else if (isAlarmed && resolution.severity) {
        action = <SeverityChip severity={resolution.severity} />;
    }

    const sx: SxProps<Theme> = [
        { height: '100%' },
        ...(isAlarmed && resolution.severity ? [buildColorTintSx(resolution.severity.color)] : []),
    ];

    return (
        <AStatusActionCard
            action={action}
            ariaLabel="inverter-simple-alarm-status"
            disabled={resolution.disabled}
            icon={icon}
            subText={resolution.subText}
            sx={sx}
            text={resolution.text}
            tone={tone}
        />
    );
};

export default InverterAlarmStatusCard;
